import React, { useState, useEffect } from 'react';
import { Download, Eye, EyeOff, Search, Plus } from 'lucide-react';
import { purchaseAPI, productAPI } from '../utils/api';
import { generatePurchasePDF, downloadPDF } from '../utils/exportUtils';
import PurchaseViewModal from '../components/PurchaseViewModal';
import PurchaseModal from '../components/PurchaseModal';
import '../styles/Purchases.css';

function Purchases() {
  const [products, setProducts] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [filteredPurchases, setFilteredPurchases] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [expandedPurchaseId, setExpandedPurchaseId] = useState(null);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);

  useEffect(() => {
    fetchPurchases();
    fetchProducts();
  }, []);

  useEffect(() => {
    if (searchQuery.trim()) {
      handleSearch();
    } else {
      setFilteredPurchases(purchases);
    }
  }, [searchQuery, purchases]);

  const fetchPurchases = async () => {
    try {
      setLoading(true);
      const response = await purchaseAPI.getAll();
      setPurchases(response.data || []);
      setFilteredPurchases(response.data || []);
    } catch (error) {
      console.error('Error fetching purchases:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await productAPI.getAll();
      setProducts(response.data || []);
    } catch (error) {
      console.error('Error fetching products for purchase form:', error);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setFilteredPurchases(purchases);
      return;
    }

    try {
      const response = await purchaseAPI.search(searchQuery);
      setFilteredPurchases(response.data || []);
    } catch (error) {
      console.error('Error searching purchases:', error);
    }
  };

  const handleViewPurchase = (purchase) => {
    setSelectedPurchase(purchase);
    setShowViewModal(true);
  };

  const handleDownloadPDF = async (purchase) => {
    try {
      const doc = generatePurchasePDF(purchase);
      downloadPDF(doc, `Purchase_${purchase.purchaseNumber}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  const toggleExpandDetails = (purchaseId) => {
    setExpandedPurchaseId(expandedPurchaseId === purchaseId ? null : purchaseId);
  };

  const handleSavePurchase = async (purchaseData) => {
    try {
      await purchaseAPI.create(purchaseData);
      setShowPurchaseModal(false);
      fetchPurchases();
      fetchProducts();
    } catch (error) {
      console.error('Error saving purchase:', error);
    }
  };

  return (
    <div className="purchases-container">
      <div className="purchases-header">
        <h1>Purchases</h1>
        <div className="purchases-toolbar">
          <div className="search-bar">
            <Search size={20} />
            <input
              type="text"
              placeholder="Search by purchase number, supplier..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <button className="btn btn-primary" onClick={() => setShowPurchaseModal(true)}>
            <Plus size={18} />
            Add Purchase
          </button>
        </div>
      </div>

      {loading && <div className="loading">Loading purchases...</div>}

      <div className="purchases-list">
        {filteredPurchases.length === 0 ? (
          <div className="empty-state">
            <p>No purchases found</p>
          </div>
        ) : (
          filteredPurchases.map((purchase) => (
            <div key={purchase.id} className="purchase-card">
              {/* Purchase Header */}
              <div className="purchase-header-row">
                <div className="purchase-info">
                  <h3>{purchase.purchaseNumber}</h3>
                  <p className="supplier-name">{purchase.supplierName}</p>
                  <p className="purchase-date">
                    {new Date(purchase.purchaseDate).toLocaleDateString('en-IN')}
                  </p>
                </div>

                <div className="purchase-amount">
                  <p className="label">Grand Total</p>
                  <p className="amount">₹{Number(purchase.grandTotal || 0).toFixed(2)}</p>
                </div>

                <div className="purchase-actions">
                  <button
                    className="btn-icon"
                    onClick={() => toggleExpandDetails(purchase.id)}
                    title={expandedPurchaseId === purchase.id ? 'Hide Details' : 'Show Details'}
                  >
                    {expandedPurchaseId === purchase.id ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                  <button
                    className="btn-icon"
                    onClick={() => handleViewPurchase(purchase)}
                    title="View Full Details"
                  >
                    <Eye size={20} />
                  </button>
                  <button
                    className="btn-icon"
                    onClick={() => handleDownloadPDF(purchase)}
                    title="Download PDF"
                  >
                    <Download size={20} />
                  </button>
                </div>
              </div>

              {/* Expandable Details Section */}
              {expandedPurchaseId === purchase.id && (
                <div className="purchase-details">
                  {/* Products Table */}
                  <div className="details-section">
                    <h4>Products Purchased</h4>
                    <table className="purchase-items-table">
                      <thead>
                        <tr>
                          <th>SL</th>
                          <th>Product Name</th>
                          <th>HSN Code</th>
                          <th>Qty</th>
                          <th>Rate</th>
                          <th>Tax %</th>
                          <th>Subtotal</th>
                          <th>Tax Amount</th>
                          <th>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {purchase.items && purchase.items.length > 0 ? (
                          purchase.items.map((item, idx) => {
                            const subtotal = item.purchasePrice * item.quantity;
                            const taxAmount = item.cgst + item.sgst + item.igst;
                            const total = subtotal + taxAmount;
                            return (
                              <tr key={idx}>
                                <td>{idx + 1}</td>
                                <td>{item.productName}</td>
                                <td>{item.hsnCode}</td>
                                <td className="text-right">{Number(item.quantity || 0).toFixed(2)}</td>
                                <td className="text-right">₹{Number(item.purchasePrice || 0).toFixed(2)}</td>
                                <td className="text-right">{Number(item.taxRate || 0)}%</td>
                                <td className="text-right">₹{Number(subtotal || 0).toFixed(2)}</td>
                                <td className="text-right">₹{Number(taxAmount || 0).toFixed(2)}</td>
                                <td className="text-right"><strong>₹{Number(total || 0).toFixed(2)}</strong></td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan="9" className="text-center">No items found</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Tax Summary Table */}
                  <div className="details-section tax-summary">
                    <h4>Tax Breakdown</h4>
                    <table className="tax-table">
                      <thead>
                        <tr>
                          <th>Tax Type</th>
                          <th>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {purchase.items && purchase.items.length > 0 && (
                          <>
                            <tr>
                              <td>CGST (Central GST)</td>
                              <td className="text-right">
                                ₹{Number(purchase.items.reduce((sum, item) => sum + (Number(item.cgst) || 0), 0)).toFixed(2)}
                              </td>
                            </tr>
                            <tr>
                              <td>SGST (State GST)</td>
                              <td className="text-right">
                                ₹{Number(purchase.items.reduce((sum, item) => sum + (Number(item.sgst) || 0), 0)).toFixed(2)}
                              </td>
                            </tr>
                            <tr>
                              <td>IGST (Integrated GST)</td>
                              <td className="text-right">
                                ₹{Number(purchase.items.reduce((sum, item) => sum + (Number(item.igst) || 0), 0)).toFixed(2)}
                              </td>
                            </tr>
                            <tr className="total-row">
                              <td><strong>Total Tax</strong></td>
                              <td className="text-right">
                                <strong>₹{Number(purchase.totalTax || 0).toFixed(2)}</strong>
                              </td>
                            </tr>
                          </>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Purchase Summary */}
                  <div className="details-section summary">
                    <div className="summary-item">
                      <span>Subtotal:</span>
                      <strong>₹{Number(purchase.subtotal || 0).toFixed(2)}</strong>
                    </div>
                    <div className="summary-item">
                      <span>Total Tax:</span>
                      <strong>₹{Number(purchase.totalTax || 0).toFixed(2)}</strong>
                    </div>
                    <div className="summary-item">
                      <span>Payment Mode:</span>
                      <strong>{purchase.paymentMode}</strong>
                    </div>
                    <div className="summary-item grand-total">
                      <span>Grand Total:</span>
                      <strong>₹{Number(purchase.grandTotal || 0).toFixed(2)}</strong>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Purchase View Modal */}
      {showViewModal && selectedPurchase && (
        <PurchaseViewModal
          purchase={selectedPurchase}
          onClose={() => setShowViewModal(false)}
        />
      )}

      {showPurchaseModal && (
        <PurchaseModal
          products={products}
          onSave={handleSavePurchase}
          onClose={() => setShowPurchaseModal(false)}
        />
      )}
    </div>
  );
}

export default Purchases;
